"use client"
import { useTranslations } from "@/lib/intl/use-translations"
import Link from "next/link"
import Image from "next/image"
import { buttonVariants } from "@/components/ui/button"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { useCountry } from "@/components/layout/country-provider"
import { isAllowedInCountry } from "@/lib/country-gates"
import type { SafeHtml } from "@/lib/safe-html"
import { blobMediaUrl } from "@/lib/blob-url"

interface FaqDetailClientProps {
  question: string
  /** Sanitized and locale-prefixed on the server (page.tsx). */
  answer: SafeHtml
  categorySlug: string
  categoryName: string
  relatedFaqs: { slug: string; question: string }[]
  featuredInArticles: { slug: string; title: string }[]
}

export function FaqDetailClient({
  question,
  answer,
  categorySlug,
  categoryName,
  relatedFaqs,
  featuredInArticles,
}: FaqDetailClientProps) {
  const ut = useTranslations("ui")
  const ft = useTranslations("faq")
  const lp = useLocalePath()
  const country = useCountry().code

  return (
    <>
      {/* Breadcrumb */}
      <nav
        className="mx-auto max-w-3xl px-6 pt-8 md:pt-12"
        aria-label="Breadcrumb"
      >
        <ol className="flex items-center gap-1.5 text-sm text-neutral-400">
          <li>
            <Link
              href={lp("/faq")}
              className="hover:text-aubergine-700 transition-colors"
            >
              FAQ
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li>
            <Link
              href={lp(`/faq/category/${categorySlug}`)}
              className="hover:text-aubergine-700 transition-colors"
            >
              {categoryName}
            </Link>
          </li>
          <li aria-hidden="true">/</li>
          <li
            className="text-aubergine-900 max-w-[200px] truncate"
            aria-current="page"
          >
            {question}
          </li>
        </ol>
      </nav>

      {/* Question + Answer */}
      <article className="mx-auto max-w-3xl px-6 pt-8 pb-16">
        <div>
          <span className="mb-4 inline-block text-xs font-medium tracking-wider text-neutral-400 uppercase">
            {categoryName}
          </span>
          <h1 className="type-display-lg text-aubergine-900">{question}</h1>
        </div>

        <div className="mt-8">
          <div className="rounded-[16px] bg-white p-6 md:p-8">
            <div
              className="[&_a]:text-aubergine-700 type-body-lg max-w-[70ch] text-neutral-600 [&_a]:underline [&_a]:underline-offset-2 [&_h5]:mt-3 [&_h5]:mb-1 [&_h5]:font-semibold [&_li]:mb-1 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
              dangerouslySetInnerHTML={{ __html: answer }}
            />
          </div>
        </div>

        {/* Featured in articles — backlinks via the new `relatedBlogPosts`
            join field on the FAQ collection. Lets readers jump to the article
            that prompted this Q&A in the first place. */}
        {featuredInArticles.length > 0 && (
          <div>
            <div className="mt-12">
              <h2 className="text-aubergine-900 mb-4 text-xl font-medium">
                {ft("featuredInArticles")}
              </h2>
              <div className="space-y-2">
                {featuredInArticles.map((post) => (
                  <Link
                    key={post.slug}
                    href={lp(`/insights/${post.slug}`)}
                    className="group flex items-center justify-between rounded-xl bg-white px-5 py-4 transition-shadow hover:shadow-sm"
                  >
                    <span className="text-aubergine-900 group-hover:text-aubergine-700 pr-4 text-sm font-medium transition-colors">
                      {post.title}
                    </span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      className="group-hover:text-aubergine-600 flex-shrink-0 text-neutral-400 transition-colors"
                    >
                      <path
                        d="M6 4l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Related questions */}
        {relatedFaqs.length > 0 && (
          <div>
            <div className="mt-12">
              <h2 className="text-aubergine-900 mb-4 text-xl font-medium">
                Related questions
              </h2>
              <div className="space-y-2">
                {relatedFaqs.map((related) => (
                  <Link
                    key={related.slug}
                    href={lp(`/faq/${related.slug}`)}
                    className="group flex items-center justify-between rounded-xl bg-white px-5 py-4 transition-shadow hover:shadow-sm"
                  >
                    <span className="text-aubergine-900 group-hover:text-aubergine-700 pr-4 text-sm font-medium transition-colors">
                      {related.question}
                    </span>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      className="group-hover:text-aubergine-600 flex-shrink-0 text-neutral-400 transition-colors"
                    >
                      <path
                        d="M6 4l4 4-4 4"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Back to all FAQs */}
        <div>
          <div className="mt-8 flex items-center gap-3">
            <Link
              href={lp("/faq")}
              className="text-aubergine-700 hover:text-aubergine-900 flex items-center gap-1.5 text-sm font-medium transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path
                  d="M10 12l-4-4 4-4"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              {ut("backToAllFaqs")}
            </Link>
            <span className="text-neutral-300">|</span>
            <Link
              href={lp(`/faq/category/${categorySlug}`)}
              className="text-aubergine-700 hover:text-aubergine-900 text-sm font-medium transition-colors"
            >
              {ut("moreInCategory", { category: categoryName })}
            </Link>
          </div>
        </div>

        {/* Bottom CTA */}
        <div>
          <div className="mt-16 rounded-[16px] bg-white p-8 text-center">
            <div className="flex justify-center -space-x-2">
              <Image
                src={blobMediaUrl("/images/avatars/ferdinand.png")}
                alt=""
                width={40}
                height={40}
                className="rounded-full border-2 border-white object-cover"
              />
              <Image
                src={blobMediaUrl("/images/avatars/dennis.png")}
                alt=""
                width={40}
                height={40}
                className="rounded-full border-2 border-white object-cover"
              />
              <Image
                src={blobMediaUrl("/images/avatars/laura.png")}
                alt=""
                width={40}
                height={40}
                className="rounded-full border-2 border-white object-cover"
              />
            </div>
            <h3 className="text-aubergine-900 mt-4 text-2xl font-bold">
              {ut("stillHaveQuestions")}
            </h3>
            <p className="mt-2 text-sm text-neutral-600">
              {ut("teamHereToHelp")}{" "}
              <Link href={lp("/contact")} className="underline">
                {ut("sendUsMessage")}
              </Link>
              .
            </p>
            <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
              {/* Discovery-call page is gated out of NL during the waitlist phase. */}
              {isAllowedInCountry("page:free-call", country) && (
                <Link href={lp("/free-call")} className={buttonVariants()}>
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    className="mr-1"
                  >
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <path d="M16 2v4M8 2v4M3 10h18" strokeLinecap="round" />
                  </svg>
                  {ut("bookAdvisoryCall")}
                </Link>
              )}
              <Link
                href={lp("/pricing")}
                className={buttonVariants({ variant: "outline" })}
              >
                {ut("seePricing")}
              </Link>
            </div>
          </div>
        </div>
      </article>
    </>
  )
}
